import React, {Dispatch, SetStateAction, useEffect, useRef, useState} from 'react'
import {Formik, Form} from 'formik'
import {Modal} from 'react-bootstrap'
import {useIntl} from 'react-intl'
import TextInput from '../../../widgets/components/Input/TextInput'
import TextArea from '../../../widgets/components/Input/TextArea'
import TagifyInput from '../../../shared-access/components/tagify-input'
import {useGetAllDropdownData} from '../../../../hooks/useGetAllDropdownData'
import {ShareholdersTypes} from '../../../stakeholders/stakeholders-and-investors/core/_constants'
import SelectSearchInput from '../../../widgets/components/Input/SelectSearchInput'
import {
  complianceRequirementTypes,
  confidentialityLevelOptions,
  docFrequencyOptions,
} from '../../core/_constants'
import * as Yup from 'yup'
import {
  createActivityLog,
  getStructureClassificationLevel1Api,
  getStructureClassificationLevel2Api,
  getStructureClassificationLevel3Api,
  updateMetadataApi,
} from '../../core/_requests'
import {useAuth} from '../../../auth'
import Skeleton from 'react-loading-skeleton'
import {toTitleCase} from '../../../../core/_util'
import {toast} from 'react-toastify'
import {getLocation} from '../../../onboarding/core/_requests'
import {useParams} from 'react-router-dom'
import {SelectInput} from '../../../widgets/components/Input/SelectInput'
import {useCurrency} from '../../../../hooks/useCurrency'

const EditMetaDataModal = ({
  isModalActive,
  setIsModalActive,
  metadata,
  metadataLoading,
  getAllMetaData,
  documentName,
}: {
  isModalActive: boolean
  setIsModalActive: Dispatch<SetStateAction<boolean>>
  metadata: any
  metadataLoading: boolean
  getAllMetaData: (recordId: string) => void
  documentName?: string
}) => {
  const {formatMessage} = useIntl()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const {countryOptions, countryLoading} = useGetAllDropdownData(ShareholdersTypes.INDIVIDUAL)
  const {currencyOptions} = useCurrency()
  const {companyId} = useAuth()
  const formikRef = useRef<any>()
  const [isLevel1Loading, setIsLevel1Loading] = useState<boolean>(false)
  const [isLevel2Loading, setIsLevel2Loading] = useState<boolean>(false)
  const {recordId} = useParams()
  const [isLevel3Loading, setIsLevel3Loading] = useState<boolean>(false)
  const [level1Options, setLevel1Options] = useState<any[]>([])
  const [level2Options, setLevel2Options] = useState<any[]>([])
  const [level3Options, setLevel3Options] = useState<any[]>([])
  const [selectedLevel1Option, setSelectedLevel1Option] = useState<any>({
    id: metadata?.structureClassificationLevelOne?.id,
    label: `Level1: ${metadata?.structureClassificationLevelOne?.name}`,
    value: metadata?.structureClassificationLevelOne?.id,
  })
  const [selectedLevel2Option, setSelectedLevel2Option] = useState<any>({
    id: metadata?.structureClassificationLevelTwo?.id,
    label: `Level2: ${metadata?.structureClassificationLevelTwo?.name}`,
    value: metadata?.structureClassificationLevelTwo?.id,
  })

  const getStructureClassificationLevel1 = async () => {
    if (!companyId) return
    setIsLevel1Loading(true)
    try {
      const {
        data: {success, data},
      } = await getStructureClassificationLevel1Api(companyId)
      if (success) {
        setLevel1Options(data)

        if (metadata?.structureClassificationLevelOne && formikRef.current) {
          formikRef.current.setFieldValue(
            'level1',
            data.find((level1: any) => level1.id === metadata?.structureClassificationLevelOne.id)
          )
        }
      }
    } catch (error) {
      console.error(error)
    } finally {
      setIsLevel1Loading(false)
    }
  }

  const getStructureClassificationLevel2 = async () => {
    if (!companyId) return
    setIsLevel2Loading(true)
    try {
      const {
        data: {success, data},
      } = await getStructureClassificationLevel2Api(
        selectedLevel1Option?.id !== metadata?.structureClassificationLevelOne.id
          ? selectedLevel1Option?.id
          : metadata?.structureClassificationLevelOne.id,
        companyId
      )
      if (success) {
        setLevel2Options(data)

        if (metadata?.structureClassificationLevelTwo && formikRef.current) {
          formikRef.current.setFieldValue(
            'level2',
            data.find((level2: any) => level2.id === metadata?.structureClassificationLevelTwo.id)
          )
        }
      }
    } catch (error) {
      console.error(error)
    } finally {
      setIsLevel2Loading(false)
    }
  }

  const getStructureClassificationLevel3 = async () => {
    if (!companyId) return
    setIsLevel3Loading(true)
    try {
      const {
        data: {success, data},
      } = await getStructureClassificationLevel3Api(
        metadata?.structureClassificationLevelTwo.id !== selectedLevel2Option?.id
          ? selectedLevel2Option?.id
          : metadata?.structureClassificationLevelTwo.id,
        companyId
      )
      if (success) {
        setLevel3Options(data)

        if (metadata?.structureClassificationLevelFour && formikRef.current) {
          formikRef.current.setFieldValue(
            'level3',
            data.find((level3: any) => level3.id === metadata?.structureClassificationLevelFour?.id)
          )
        }
      }
    } catch (error) {
      console.error(error)
    } finally {
      setIsLevel3Loading(false)
    }
  }

  useEffect(() => {
    getStructureClassificationLevel1()
  }, [metadata?.structureClassificationLevelOne]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    getStructureClassificationLevel2()
  }, [metadata?.structureClassificationLevelTwo, selectedLevel1Option?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    getStructureClassificationLevel3()
  }, [metadata?.structureClassificationLevelFour, selectedLevel2Option?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const initialValues = {
    documentName: documentName || null,
    serialNumber: metadata?.serialNumber || null,
    documentNature: metadata?.documentNature ? metadata?.documentNature.split(',') : null,
    country:
      metadata?.country && countryOptions.length > 0
        ? countryOptions.find((country) => country.label === metadata?.country)
        : null,
    summary: metadata?.summary || null,
    context: metadata?.context || null,
    keywords: metadata?.keywords ? metadata?.keywords.split(',') : null,
    level1: level1Options.find(
      (level1: any) => level1.id === metadata?.structureClassificationLevelOne.id
    ),
    level2: level2Options.find(
      (level2: any) => level2.id === metadata?.structureClassificationLevelTwo.id
    ),
    level3: level3Options.find(
      (level3: any) => level3.id === metadata?.structureClassificationLevelThree.id
    ),
    referencedCompanies: metadata?.referencedCompanies
      ? metadata?.referencedCompanies.split(',')
      : null,
    associatedSoftware: metadata?.associatedSoftware || null,
    associatedSoftwareVersion: metadata?.associatedSoftwareVersion || null,
    tags: metadata?.tags ? metadata?.tags.split(',') : null,
    persons: metadata?.persons ? metadata?.persons.split(',') : null,
    confidentialityLevel: metadata?.confidentialityLevel
      ? {
          id: metadata?.confidentialityLevel,
          label: toTitleCase(metadata?.confidentialityLevel),
          value: metadata?.confidentialityLevel,
        }
      : null,
    relevantDates: metadata?.relevantDates ? metadata?.relevantDates.split(',') : null,
    complianceRequirement: metadata?.complianceRequirement
      ? complianceRequirementTypes.find(
          (compliance) => compliance.value === metadata?.complianceRequirement
        )
        ? complianceRequirementTypes.find(
            (compliance) => compliance.value === metadata?.complianceRequirement
          )
        : metadata?.complianceRequirement.split(';').map((compliance: any) => ({
            id: complianceRequirementTypes.length + 1,
            label: compliance,
            value: compliance,
          }))
      : null,
    docDate: metadata?.docDate || null,
    signingDate: metadata?.signingDate || null,
    expirationDate: metadata?.expirationDate || null,
    startDate: metadata?.startDate || null,
    endDate: metadata?.endDate || null,
    docFrequency: metadata?.frequency
      ? docFrequencyOptions.find((freq: any) => freq.value === metadata?.frequency)?.value
      : null,
  }

  const validationSchema = Yup.object().shape({
    documentName: Yup.string().required(formatMessage({id: 'Document Name: Required'})),
    level1: Yup.object()
      .nullable()
      .required(formatMessage({id: 'Level1: Required'})),
    level2: Yup.object()
      .nullable()
      .required(formatMessage({id: 'Level2: Required'})),
    level3: Yup.object()
      .nullable()
      .required(formatMessage({id: 'level3: Required'})),
  })
  const getDeviceType = () => {
    const ua = navigator.userAgent

    if (
      /Mobile|iP(hone|od|ad)|Android|BlackBerry|IEMobile|Kindle|NetFront|Silk-Accelerated|(hpw|web)OS|Fennec|Minimo|Opera Mobi|Blazer|Dolfin|Dolphin|Skyfire|Zune/.test(
        ua
      )
    ) {
      return 'MOBILE'
    }
    return 'WEB'
  }

  const getOS = () => {
    const ua = navigator.userAgent
    if (/iPhone|iPad|iPod|Macintosh/.test(ua)) return 'APPLE'
    if (/Win/.test(ua)) return 'WINDOWS'
    if (/android/i.test(ua)) return 'ANDROID'
    return 'Unknown'
  }
  const createActivity = async (recordId: any) => {
    const os = getOS()
    const deviceTypeView = getDeviceType()
    const locationApiData = await getLocation()
    const city = locationApiData?.data.country_name
    const ipAddress = locationApiData?.data?.ip
    try {
      if (deviceTypeView && os && city && recordId && companyId) {
        const {
          data: {success, errors},
        } = await createActivityLog(
          companyId,
          recordId,
          os,
          deviceTypeView,
          city,
          ipAddress,
          'EDITED'
        )
        if (!success) {
          errors.forEach((error: string) => {
            toast.error(formatMessage({id: error}))
          })
        }
      }
    } catch (err) {
      console.log(err)
    }
  }
  const handleSubmit = async (values: any, {resetForm}: any) => {
    setIsLoading(true)
    values = {
      ...values,
      complianceRequirement: values.complianceRequirement
        ?.map((compliance: any) => compliance.value)
        .join(';'),
      confidentialityLevel: values.confidentialityLevel?.value,
      persons: Array.isArray(values.persons) ? values.persons.join(',') : values.persons,
      keywords: Array.isArray(values.keywords) ? values.keywords.join(', ') : values.keywords,
      level1: values.level1?.value,
      level2: values.level2?.value,
      level3Year: values.level3Year?.value,
      level3Month: values.level3Month?.value,
      level3: values.level3?.value,
      country: values.country?.label,
      tags: Array.isArray(values.tags) ? values.tags.join(',') : values.tags,
      documentNature: Array.isArray(values.documentNature)
        ? values.documentNature.join(',')
        : values.documentNature,
      relevantDates: Array.isArray(values.relevantDates)
        ? values.relevantDates.join(',')
        : values.relevantDates,
      referencedCompanies: Array.isArray(values.referencedCompanies)
        ? values.referencedCompanies.join(',')
        : values.referencedCompanies,
      docFrequency: values.docFrequency ? values.docFrequency.value : null,
    }

    Object.keys(values).forEach((key) => {
      if (values[key] == null) {
        delete values[key]
      }
    })

    if (!companyId) return
    if (!recordId) return
    try {
      const {
        data: {success, errors},
      } = await updateMetadataApi(metadata.fileVersionId, values, companyId)
      if (success) {
        toast.success(formatMessage({id: `${metadata.fileName} metadata updated successfully`}))
        createActivity(recordId)
        setIsModalActive(false)
        getAllMetaData(recordId)
        resetForm()
      } else {
        toast.error(errors[0])
      }
    } catch (error) {
      toast.error(formatMessage({id: 'Something went wrong'}))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        innerRef={formikRef}
        enableReinitialize={true}
        validateOnMount={true}
      >
        {(formik) => {
          return (
            <Form>
              <Modal
                show={isModalActive}
                onHide={() => {
                  setIsModalActive(false)
                }}
                fullscreen
              >
                <Modal.Header closeButton>
                  <div className='d-flex flex-column'>
                    <h2>{formatMessage({id: 'Edit Metadata'})}</h2>
                  </div>
                </Modal.Header>
                <Modal.Body>
                  <div className='w-100 container'>
                    <div className='row'>
                      <h3 className='mb-4'>{formatMessage({id: 'File Information'})}</h3>
                      {metadataLoading ? (
                        <div className='col-xl-12 d-md-flex'>
                          <div className='mt-md-8 w-100 px-5 px-md-0 mb-4'>
                            <div className='d-md-flex col-md-12 flex-wrap gap-6 mb-10'>
                              <div className='col-md-5 mb-10 mb-md-0'>
                                <div className='w-50'>
                                  <Skeleton
                                    height={20}
                                    baseColor='var(--kt-skeleton-bg-color)'
                                    highlightColor='var(--kt-skeleton-highlight-bg-color)'
                                  />
                                </div>
                                <div className='mt-2'>
                                  <Skeleton
                                    height={38}
                                    baseColor='var(--kt-skeleton-bg-color)'
                                    highlightColor='var(--kt-skeleton-highlight-bg-color)'
                                  />
                                </div>
                              </div>
                              <div className='col-md-5'>
                                <div className='w-50'>
                                  <Skeleton
                                    height={20}
                                    baseColor='var(--kt-skeleton-bg-color)'
                                    highlightColor='var(--kt-skeleton-highlight-bg-color)'
                                  />
                                </div>
                                <div className='mt-2'>
                                  <Skeleton
                                    height={38}
                                    baseColor='var(--kt-skeleton-bg-color)'
                                    highlightColor='var(--kt-skeleton-highlight-bg-color)'
                                  />
                                </div>
                              </div>
                            </div>
                            <div className='d-md-flex col-md-12 flex-wrap gap-6 mb-10'>
                              <div className='col-md-5 mb-10 mb-md-0'>
                                <div className='w-50'>
                                  <Skeleton
                                    height={20}
                                    baseColor='var(--kt-skeleton-bg-color)'
                                    highlightColor='var(--kt-skeleton-highlight-bg-color)'
                                  />
                                </div>
                                <div className='mt-2'>
                                  <Skeleton
                                    height={38}
                                    baseColor='var(--kt-skeleton-bg-color)'
                                    highlightColor='var(--kt-skeleton-highlight-bg-color)'
                                  />
                                </div>
                              </div>
                              <div className='col-md-5'>
                                <div className='w-50'>
                                  <Skeleton
                                    height={20}
                                    baseColor='var(--kt-skeleton-bg-color)'
                                    highlightColor='var(--kt-skeleton-highlight-bg-color)'
                                  />
                                </div>
                                <div className='mt-2'>
                                  <Skeleton
                                    height={38}
                                    baseColor='var(--kt-skeleton-bg-color)'
                                    highlightColor='var(--kt-skeleton-highlight-bg-color)'
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <>
                          {/* <div className='col-md-6 col-12'>
                            <TextInput
                              fieldType='text'
                              label={formatMessage({ id: 'Document Name' })}
                              fieldName='documentName'
                              formik={formik}
                              placeholder={formatMessage({ id: 'Enter Document Name' })}
                              toolTipText={formatMessage({
                                id: 'Enter the name of the document for easy identification and searchability.',
                              })}
                              isTooltipForModal={true}
                            />
                          </div> */}
                          <div className='col-md-6 col-12 mb-8 mb-md-auto'>
                            <TagifyInput
                              label={formatMessage({id: 'Keywords'})}
                              placeholder={formatMessage({id: 'Enter keywords'})}
                              tooltip={formatMessage({
                                id: `Provide the essential terms that describe this document's content. Adding relevant keywords improves searchability and helps organize your records efficiently.`,
                              })}
                              isTooltipRequired
                              fieldName='keywords'
                              formik={formik}
                            />
                          </div>
                          <div className='col-md-6 col-12'>
                            <TextInput
                              fieldType='text'
                              label={formatMessage({id: 'Serial Number'})}
                              fieldName='serialNumber'
                              formik={formik}
                              placeholder={formatMessage({id: 'Enter Serial number'})}
                              toolTipText={formatMessage({
                                id: `Enter a unique identifier for this document in sequential order. Critical for tracking invoices, contracts, and other numbered documents in your workflow.`,
                              })}
                              isTooltipForModal={true}
                            />
                          </div>
                          <div className='col-md-6 col-12 mb-8'>
                            <TagifyInput
                              label={formatMessage({id: 'Document Nature'})}
                              placeholder={formatMessage({id: 'Enter other affiliations'})}
                              tooltip={formatMessage({
                                id: `Classify the document type here. Selecting accurate categories ensures proper workflow routing and compliance with record management policies.`,
                              })}
                              isTooltipRequired
                              fieldName='documentNature'
                              formik={formik}
                            />
                          </div>
                          <div className='col-md-6 col-12 mb-8'>
                            <TagifyInput
                              label={formatMessage({id: 'Tags'})}
                              placeholder={formatMessage({
                                id: 'Enter Referenced Companies e.g., IT, Finance',
                              })}
                              isTooltipRequired
                              tooltip={formatMessage({
                                id: `Enter the labels that categorize this document by department or function. Tags improve findability and enable filtered views of related documents.`,
                              })}
                              fieldName='tags'
                              formik={formik}
                            />
                          </div>
                          {countryLoading ? (
                            <div className='col-md-6 col-12 mb-8'>
                              <Skeleton
                                height={40}
                                baseColor='var(--kt-skeleton-bg-color)'
                                highlightColor='var(--kt-skeleton-highlight-bg-color)'
                              />
                            </div>
                          ) : (
                            <div className='col-md-6 col-12'>
                              <SelectSearchInput
                                label={formatMessage({id: 'Related Country'})}
                                fieldName='country'
                                placeholder={formatMessage({id: 'Select Country'})}
                                formik={formik}
                                isTooltipForModal
                                toolTipText={formatMessage({
                                  id: `Select the jurisdiction country relevant to this document. Important for compliance with country-specific regulations and international business operations.`,
                                })}
                                options={countryOptions}
                              />
                            </div>
                          )}
                        </>
                      )}
                      <h3 className='mb-4'>{formatMessage({id: 'AI-Powered Period Details'})}</h3>

                      <div className='col-md-6 col-12'>
                        <TextInput
                          fieldType='date'
                          label={formatMessage({id: 'Date'})}
                          fieldName='docDate'
                          formik={formik}
                          placeholder={formatMessage({id: 'Select Document Date'})}
                          toolTipText={formatMessage({
                            id: `Enter the date when this document was officially created. It establishes the legal timeline for contracts, agreements, and authorized documents.`,
                          })}
                          isTooltipForModal={true}
                        />
                      </div>

                      <div className='col-md-6 col-12'>
                        <TextInput
                          fieldType='date'
                          label={formatMessage({id: 'Signing Date'})}
                          fieldName='signingDate'
                          formik={formik}
                          placeholder={formatMessage({id: 'Select Signing Date'})}
                          toolTipText={formatMessage({
                            id: `Enter the date when this document was officially executed. It establishes the legal timeline for contracts, agreements, and authorized documents.`,
                          })}
                          isTooltipForModal={true}
                        />
                      </div>
                      <div className='col-md-6 col-12'>
                        <TextInput
                          fieldType='date'
                          label={formatMessage({id: 'Expiration Date'})}
                          fieldName='expirationDate'
                          formik={formik}
                          placeholder={formatMessage({id: 'Select Expiration Date'})}
                          toolTipText={formatMessage({
                            id: `Specify the date when this document ceases to be valid. Critical for managing renewal cycles and preventing the use of outdated documents.`,
                          })}
                          isTooltipForModal={true}
                        />
                      </div>
                      <div className='col-md-6 col-12'>
                        <SelectSearchInput
                          label={formatMessage({id: 'Frequency'})}
                          fieldName='docFrequency'
                          placeholder={formatMessage({
                            id: 'Select Frequency',
                          })}
                          formik={formik}
                          toolTipText={formatMessage({
                            id: `Specify how often this document type recurs. Helps in planning review cycles and anticipating related documentation needs.`,
                          })}
                          options={docFrequencyOptions}
                          isTooltipForModal={true}
                        />
                      </div>
                      <div className='col-md-6 col-12'>
                        <TextInput
                          fieldType='date'
                          label={formatMessage({id: 'Start Date'})}
                          fieldName='startDate'
                          formik={formik}
                          placeholder={formatMessage({id: 'Select Start Date'})}
                          toolTipText={formatMessage({
                            id: `Specify when this document or agreement becomes effective. Essential for tracking active contracts and implementation timelines.`,
                          })}
                          isTooltipForModal={true}
                        />
                      </div>
                      <div className='col-md-6 col-12'>
                        <TextInput
                          fieldType='date'
                          label={formatMessage({id: 'End Date'})}
                          fieldName='endDate'
                          formik={formik}
                          placeholder={formatMessage({id: 'Select End Date'})}
                          toolTipText={formatMessage({
                            id: `Enter the date when this document's validity concludes. Important for contract management and planning renewal or replacement activities.`,
                          })}
                          isTooltipForModal={true}
                        />
                      </div>

                      <h3 className='mb-4'>{formatMessage({id: 'AI-Powered Insights'})}</h3>
                      <div className='col-md-12 col-12'>
                        <TextArea
                          label={formatMessage({id: 'Summary'})}
                          fieldName='summary'
                          formik={formik}
                          placeholder={formatMessage({id: 'Enter Summary'})}
                          toolTipText={formatMessage({
                            id: `Provide the overview of key document points. This saves time by highlighting essential information without reading the entire document.`,
                          })}
                          isTooltipForModal={true}
                        />
                      </div>
                      <div className='col-md-6 col-12'>
                        <TextInput
                          fieldType='text'
                          label={formatMessage({id: 'Document Context'})}
                          fieldName='context'
                          formik={formik}
                          placeholder={formatMessage({id: 'Enter Document Context'})}
                          toolTipText={formatMessage({
                            id: `Provide the main content of the document here. It helps quickly understand the document's purpose and most important details.`,
                          })}
                          isTooltipForModal={true}
                        />
                      </div>
                      <div className='col-md-6 col-12 mb-8 mb-md-auto'>
                        <TagifyInput
                          label={formatMessage({id: 'Keywords'})}
                          placeholder={formatMessage({id: 'Enter keywords'})}
                          tooltip={formatMessage({
                            id: `Enter the relevant and significant terms from the document content. Improves document discovery through intelligent search capabilities.`,
                          })}
                          isTooltipForModal
                          isTooltipRequired
                          fieldName='keywords'
                          formik={formik}
                        />
                      </div>
                      <div className='col-md-12 col-12 w-100'>
                        <div className='d-flex flex-md-row flex-column align-items-md-end align-items-start  col-md-12'>
                          {isLevel1Loading ? (
                            <div className='d-flex flex-column mb-8 '>
                              <div>
                                <label className='d-flex form-label font-size-12 text-dark text-capitalize ms-0 ms-md-0'>
                                  {formatMessage({id: 'Structure Classification'})}
                                </label>
                              </div>
                              <div className='d-flex align-items-center '>
                                <div className='d-flex align-items-center'>
                                  <Skeleton
                                    height={38}
                                    width={'20vw'}
                                    baseColor='var(--kt-skeleton-bg-color)'
                                    highlightColor='var(--kt-skeleton-highlight-bg-color)'
                                  />
                                </div>
                                <div className='mx-4'>{'>'}</div>
                              </div>
                            </div>
                          ) : (
                            <div
                              className={`d-flex align-items-center w-100 w-md-30 gap-md-3 gap-0  col-md-4`}
                            >
                              <SelectSearchInput
                                label={formatMessage({id: 'Structure Classification'})}
                                fieldName='level1'
                                width={10}
                                placeholder={formatMessage({
                                  id: 'Select Level 1',
                                })}
                                formik={formik}
                                toolTipText={formatMessage({
                                  id: `Specify the multi-level organizational hierarchy for this document. Ensures consistent categorization and proper placement in your records system.`,
                                })}
                                options={level1Options}
                                isTooltipForModal={true}
                                setSelectedLevel1Option={setSelectedLevel1Option}
                              />
                              <div className='mx-4'>{'>'}</div>
                            </div>
                          )}
                          {isLevel2Loading ? (
                            <div className='d-flex flex-column mb-8'>
                              <div className='d-flex align-items-center mb-2'>
                                <div className='d-flex align-items-center'>
                                  <Skeleton
                                    height={38}
                                    width={'20vw'}
                                    baseColor='var(--kt-skeleton-bg-color)'
                                    highlightColor='var(--kt-skeleton-highlight-bg-color)'
                                  />
                                </div>
                                <div className='mx-4'>{'>'}</div>
                              </div>
                            </div>
                          ) : (
                            <div
                              className={`d-flex align-items-center w-100 w-md-30 gap-md-3 gap-0  col-md-4`}
                            >
                              <SelectSearchInput
                                label=''
                                fieldName='level2'
                                placeholder={formatMessage({
                                  id: 'Select Level 2',
                                })}
                                formik={formik}
                                width={10}
                                options={level2Options}
                                isTooltipForModal={false}
                                setSelectedLevel2Option={setSelectedLevel2Option}
                              />
                              <div className='mx-4 mb-8'>{'>'}</div>
                            </div>
                          )}

                          {isLevel3Loading ? (
                            <div className='d-flex flex-column mb-8 '>
                              <div className='mt-3' />
                              <div className='d-flex align-items-center mb-2'>
                                <div className='d-flex align-items-center'>
                                  <Skeleton
                                    height={38}
                                    width={'20vw'}
                                    baseColor='var(--kt-skeleton-bg-color)'
                                    highlightColor='var(--kt-skeleton-highlight-bg-color)'
                                  />
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div
                              className={`d-flex align-items-center w-100 w-md-30 gap-md-3 gap-0  col-md-4`}
                            >
                              <SelectSearchInput
                                label=''
                                fieldName='level3'
                                placeholder={formatMessage({
                                  id: 'Select Level 3',
                                })}
                                formik={formik}
                                width={10}
                                options={level3Options}
                                isTooltipForModal={false}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                      <div className='col-md-6 col-12'>
                        <SelectInput
                          label={formatMessage({id: 'Currency'})}
                          fieldName={'currency'}
                          placeholder={formatMessage({id: 'Select the currency'})}
                          formik={formik}
                          toolTipText={formatMessage({
                            id: `Select the monetary unit used in financial figures. Crucial for accurate financial reporting and multi-currency operations.`,
                          })}
                          // options={['INR', 'USD']}
                          options={currencyOptions}
                          isTooltipForModal
                        />
                      </div>
                      <div className='col-md-6 col-12'>
                        <TextInput
                          fieldType='number'
                          label={formatMessage({id: 'Amount'})}
                          fieldName='amount'
                          formik={formik}
                          placeholder={formatMessage({id: 'Enter Amount'})}
                          toolTipText={formatMessage({
                            id: `Provide the financial value associated with this document. Essential for budget tracking, financial audits, and accounting reconciliation.`,
                          })}
                          isTooltipForModal={true}
                        />
                      </div>
                      <div className='col-md-6 col-12'>
                        <SelectSearchInput
                          label={formatMessage({id: 'Country'})}
                          fieldName='country'
                          placeholder={formatMessage({id: 'Select Country'})}
                          formik={formik}
                          toolTipText={formatMessage({
                            id: `Select the country relevant to this document. Important for tax jurisdiction, legal compliance, and regional business activities.`,
                          })}
                          options={countryOptions}
                          isTooltipForModal
                        />
                      </div>
                      <div className='col-md-6 col-12'>
                        <TextInput
                          fieldType='text'
                          label={formatMessage({id: 'Associated Software'})}
                          fieldName='associatedSoftware'
                          formik={formik}
                          placeholder={formatMessage({
                            id: 'Enter Associated Software e.g., MS Word, Excel, Adobe PDF',
                          })}
                          toolTipText={formatMessage({
                            id: `Specify the application used to create this document. Important for compatibility, viewing, and long-term accessibility planning.`,
                          })}
                          isTooltipForModal={true}
                        />
                      </div>
                      <div className='col-md-6 col-12'>
                        <TextInput
                          fieldType='text'
                          label={formatMessage({id: 'Associated Software version'})}
                          fieldName='associatedSoftwareVersion'
                          formik={formik}
                          placeholder={formatMessage({
                            id: 'Enter Associated Software Version e.g., 1.0, 2.0',
                          })}
                          toolTipText={formatMessage({
                            id: `Provide specific software version used here. Critical for future access to legacy documents and format compatibility.`,
                          })}
                          isTooltipForModal={true}
                        />
                      </div>
                      <div className='col-md-6 col-12 mb-8'>
                        <TagifyInput
                          label={formatMessage({id: 'Referenced Companies'})}
                          placeholder={formatMessage({
                            id: 'Enter Referenced Companies e.g., Google, Microsoft',
                          })}
                          isTooltipRequired
                          tooltip={formatMessage({
                            id: `Specify the organizations mentioned within the document. Automatically identifies business relationships and network connections.`,
                          })}
                          fieldName='referencedCompanies'
                          formik={formik}
                        />
                      </div>
                      <div className='col-md-6 col-12'>
                        <SelectSearchInput
                          label={formatMessage({id: 'Confidentiality Level'})}
                          fieldName='confidentialityLevel'
                          placeholder={formatMessage({
                            id: 'Select Confidentiality Level',
                          })}
                          formik={formik}
                          toolTipText={formatMessage({
                            id: `Set the security classification for controlling document access. Essential for protecting sensitive information and compliance with data protection regulations.`,
                          })}
                          options={confidentialityLevelOptions}
                          isTooltipForModal={true}
                        />
                      </div>
                      <div className='col-md-6 col-12 mb-8'>
                        <TagifyInput
                          label={formatMessage({id: 'Tags'})}
                          placeholder={formatMessage({
                            id: 'Enter Referenced Companies e.g., IT, Finance',
                          })}
                          isTooltipRequired
                          tooltip={formatMessage({
                            id: `Provide the categorization labels relevant for this document. Enhances organization and retrieval through intelligent classification.`,
                          })}
                          fieldName='tags'
                          formik={formik}
                        />
                      </div>
                      <div className='col-md-6 col-12 mb-8'>
                        <TagifyInput
                          label={formatMessage({id: 'Entities, if Any'})}
                          placeholder={formatMessage({
                            id: 'Enter Entities e.g., John Doe: Accountant',
                          })}
                          isTooltipRequired
                          tooltip={formatMessage({
                            id: `Specify the people or organizations identified in the document. Automatically maps relationships between documents and stakeholders.`,
                          })}
                          fieldName='persons'
                          formik={formik}
                        />
                      </div>
                      <div className='col-md-6 col-12 mb-8'>
                        <TagifyInput
                          label={formatMessage({id: 'Relevant Dates'})}
                          placeholder={formatMessage({
                            id: 'Enter Relevant Dates e.g., Start Date: 24 Sept 2024,',
                          })}
                          isTooltipRequired
                          tooltip={formatMessage({
                            id: `Enter the key timeframes mentioned that may require attention. Helps track important deadlines and action items from document content.`,
                          })}
                          fieldName='relevantDates'
                          formik={formik}
                        />
                      </div>
                      <div className='col-md-6 col-12 mb-8'>
                        <SelectSearchInput
                          label={formatMessage({id: 'Compliance Requirements'})}
                          fieldName='complianceRequirement'
                          placeholder={formatMessage({
                            id: 'Select Compliance Requirement',
                          })}
                          formik={formik}
                          toolTipText={formatMessage({
                            id: `Specify the regulatory standards this document must satisfy. Ensures your records meet legal obligations and reduces compliance risks.`,
                          })}
                          options={complianceRequirementTypes}
                          isTooltipForModal={true}
                          isMulti={true}
                        />
                      </div>
                    </div>
                  </div>
                </Modal.Body>
                <Modal.Footer className='px-0 container'>
                  <div className='d-md-flex w-100 w-md-auto justify-content-end px-md-0 px-0 gap-3'>
                    <div className='w-100 order-1 order-md-2 mb-2 mb-md-auto'>
                      <button
                        type='button'
                        className=' btn bg-primary text-white custom-button-property w-100'
                        disabled={metadataLoading || isLoading || !formik.isValid}
                        onClick={formik.submitForm}
                      >
                        {isLoading ? (
                          <span className='indicator-label font-size-13 p-0'>
                            {formatMessage({id: 'Please wait...'})}
                            <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                          </span>
                        ) : (
                          <span className='indicator-label font-size-13 p-0'>
                            {formatMessage({id: 'Save Changes'})}
                          </span>
                        )}
                      </button>
                    </div>
                    <div className='w-100 w-md-auto order-2 order-md-1 '>
                      <button
                        type='button'
                        className='btn bg-light custom-button-property text-muted w-100'
                        onClick={() => setIsModalActive(false)}
                        disabled={isLoading}
                      >
                        {formatMessage({id: 'Cancel'})}
                      </button>
                    </div>
                  </div>
                </Modal.Footer>
              </Modal>
            </Form>
          )
        }}
      </Formik>
    </div>
  )
}

export default EditMetaDataModal
